const MECHANIC_SUFFIXES = [
  [3, "citation-path-trace"],
  [4, "find-the-gap-page-inspection"],
  [5, "interactive-audit-map"],
  [6, "live-calculator-model"],
  [7, "scope-budget-dial-with-safe-ba"],
  [8, "scored-readiness-checklist-int"],
  [9, "scored-self-assessment-fit-qui"],
  [10, "service-fit-mini-game"],
  [11, "encouraging-inline-feedback-se"],
  [12, "multi-step-configurator-planne"],
  [13, "mini-self-check-light-interact"],
  [14, "inline-before-after-reveal-lig"],
] as const;

const GAME_SURFACE_SUFFIXES = MECHANIC_SUFFIXES.slice(1);
const READING_SUPPORT_SUFFIXES = [
  [2, "reading-progress-section-compl"],
  [3, "key-points-self-check-calm-rea"],
  [4, "before-after-ai-answer-reveal-"],
] as const;
const CTA_SUPPORT_SUFFIXES = [
  [2, "earned-result-cta-reveals-a-pe"],
  [3, "value-exchange-cta-support"],
  [4, "readiness-check-cta"],
] as const;
const OFFER_ASSEMBLY_SUFFIX = [[2, "offer-assembly-configurator"]] as const;
const FORM_FIELD_SUFFIXES = [
  [1, "encouraging-inline-validation-"],
  [2, "chunked-multi-step-form-progre"],
] as const;
const DISCLOSURE_PANEL_SUFFIXES = [
  ["P2", 1, "before-after-ai-answer-reveal-"],
  ["P3", 2, "interactive-audit-map-section"],
  ["P3", 3, "decision-path-section"],
  ["P3", 4, "find-the-gap-section-inspectio"],
  ["P3", 5, "recommended-next-step-framing-"],
  ["P3", 7, "on-scroll-reveal-support"],
] as const;

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function ticketNumber(value: number): string {
  return String(value).padStart(6, "0");
}

function stepNumber(value: number): string {
  return String(value).padStart(3, "0");
}

function groupedTicket(prefix: number, suffixIndex: number): string {
  return ticketNumber((prefix * 1000) + suffixIndex);
}

export const STEP_ONE_NEXT200_GRID_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${ticketNumber(rank)}-interactive-step-01-layout-planner-game-grid-001-${suffix}`);

export const STEP_ONE_NEXT200_GAME_PREVIEW_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${ticketNumber(1000 + rank)}-interactive-step-01-interactive-game-preview-planner-${suffix}`);

export const STEP_ONE_NEXT200_GAME_IFRAME_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${ticketNumber(2000 + rank)}-interactive-step-01-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_ONE_NEXT200_READING_TICKETS = [
  ...range(3, 4).flatMap((prefix) =>
    READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
      `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-01-heading-planner-step-${stepNumber(prefix + 1)}-${suffix}`)),
  ...range(5, 23).flatMap((prefix) => [
    `P2-${groupedTicket(prefix, 1)}-reading-step-01-heading-planner-step-${stepNumber(prefix + 1)}-before-after-ai-answer-reveal-`,
    ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
      `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-01-heading-planner-step-${stepNumber(prefix + 1)}-${suffix}`),
  ]),
];

export const STEP_ONE_NEXT200_CTA_TICKETS = [
  ...CTA_SUPPORT_SUFFIXES.slice(1).map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(24, suffixIndex)}-cta-step-01-button-planner-action-025-${suffix}`),
  ...range(25, 46).flatMap((prefix) =>
    CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
      `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-01-button-planner-action-${stepNumber(prefix + 1)}-${suffix}`)),
];

export const STEP_ONE_NEXT200_NAV_TICKETS = range(47, 84).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-01-link-navigation-${stepNumber(prefix + 1)}-subtle-active-state-cue-suppor`);

export const STEP_ONE_FIELD_INPUT_TICKETS = range(85, 91).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-01-field-planner-input-${stepNumber(prefix + 1)}-${suffix}`));

export const STEP_ONE_DISCLOSURE_PANEL_TICKETS = range(92, 97).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-01-disclosure-panel-${stepNumber(prefix + 1)}-${suffix}`));

export const STEP_ONE_PLANNER_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(98, rank)}-interactive-step-01-planner-game-grid-${suffix}`);

export const STEP_ONE_PLANNER_STEP_HEADER_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(99, rank)}-interactive-step-01-planner-step-header-${suffix}`);

export const STEP_ONE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(100, rank)}-interactive-step-01-planner-control-panel-${suffix}`);

export const STEP_ONE_GAME_PREVIEW_SUPPORT_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(101, rank)}-interactive-step-01-game-preview-${suffix}`);

export const STEP_ONE_GAME_IFRAME_SUPPORT_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(102, rank)}-interactive-step-01-game-iframe-${suffix}`);

export const STEP_ONE_XP_HUD_SUPPORT_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(103, rank)}-interactive-step-01-xp-hud-${suffix}`);

export const STEP_ONE_STEP_NAV_SUPPORT_TICKETS = [
  "P3-104002-nav-step-01-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_ONE_TEMPLATE_PICKER_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(105, rank)}-interactive-step-01-template-picker-${suffix}`);

export const STEP_ONE_PERSONA_SELECTOR_SUPPORT_TICKETS = [
  "P3-106002-value-prop-step-01-persona-selector-service-fit-mini-game-value-pr",
  "P3-106003-value-prop-step-01-persona-selector-competence-framed-benefit-chec",
  "P3-106004-value-prop-step-01-persona-selector-before-after-ai-answer-reveal-",
] as const;

export const STEP_TWO_LAYOUT_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(107, rank)}-interactive-step-02-layout-planner-game-grid-001-${suffix}`);

export const STEP_TWO_GAME_PREVIEW_SUPPORT_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(108, rank)}-interactive-step-02-interactive-game-preview-planner-${suffix}`);

export const STEP_TWO_GAME_IFRAME_SUPPORT_TICKETS = GAME_SURFACE_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(109, rank)}-interactive-step-02-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_TWO_READING_TICKETS = range(110, 130).flatMap((prefix) => [
  `P2-${groupedTicket(prefix, 1)}-reading-step-02-heading-planner-step-${stepNumber(prefix - 106)}-before-after-ai-answer-reveal-`,
  ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-02-heading-planner-step-${stepNumber(prefix - 106)}-${suffix}`),
]);

export const STEP_TWO_CTA_TICKETS = range(131, 139).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-02-button-planner-action-${stepNumber(prefix - 106)}-${suffix}`));

export const STEP_TWO_BATCH2_CTA_TICKETS = range(140, 152).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-02-button-planner-action-${stepNumber(prefix - 106)}-${suffix}`));

export const STEP_TWO_BATCH2_NAV_TICKETS = range(153, 190).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-02-link-navigation-${stepNumber(prefix - 106)}-subtle-active-state-cue-suppor`);

export const STEP_TWO_STEP_NAVIGATION_TICKETS = [
  "P3-210001-nav-step-02-step-navigation-leave-as-is-keep-nav-clean",
  "P3-210002-nav-step-02-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_TWO_FIELD_INPUT_TICKETS = range(191, 197).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-02-field-planner-input-${stepNumber(prefix - 106)}-${suffix}`));

export const STEP_TWO_DISCLOSURE_PANEL_TICKETS = range(198, 203).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-02-disclosure-panel-${stepNumber(prefix - 106)}-${suffix}`));

export const STEP_TWO_PLANNER_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(204, rank)}-interactive-step-02-planner-game-grid-${suffix}`);

export const STEP_TWO_PLANNER_STEP_HEADER_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(205, rank)}-interactive-step-02-planner-step-header-${suffix}`);

export const STEP_TWO_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(206, rank)}-interactive-step-02-planner-control-panel-${suffix}`);

export const STEP_TWO_GAME_PREVIEW_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(207, rank)}-interactive-step-02-game-preview-${suffix}`);

export const STEP_TWO_GAME_IFRAME_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(208, rank)}-interactive-step-02-game-iframe-${suffix}`);

export const STEP_TWO_XP_HUD_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(209, rank)}-interactive-step-02-xp-hud-${suffix}`);

export const STEP_TWO_TRAFFIC_CHANNEL_LIST_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(211, rank)}-interactive-step-02-traffic-channel-list-${suffix}`);

export const STEP_TWO_VISITOR_SLIDER_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(212, rank)}-interactive-step-02-visitor-sliders-${suffix}`);

export const STEP_TWO_TRAFFIC_CITY_DISTRICT_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(213, rank)}-interactive-step-02-traffic-city-district-${suffix}`);

export const STEP_THREE_LAYOUT_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(214, rank)}-interactive-step-03-layout-planner-game-grid-001-${suffix}`);

export const STEP_THREE_GAME_PREVIEW_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(215, rank)}-interactive-step-03-interactive-game-preview-planner-${suffix}`);

export const STEP_THREE_GAME_IFRAME_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(216, rank)}-interactive-step-03-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_THREE_READING_TICKETS = range(217, 237).flatMap((prefix) => [
  `P2-${groupedTicket(prefix, 1)}-reading-step-03-heading-planner-step-${stepNumber(prefix - 213)}-before-after-ai-answer-reveal-`,
  ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-03-heading-planner-step-${stepNumber(prefix - 213)}-${suffix}`),
]);

export const STEP_THREE_CTA_TICKETS = range(238, 252).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-03-button-planner-action-${stepNumber(prefix - 213)}-${suffix}`));

export const STEP_THREE_NAV_TICKETS = range(253, 290).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-03-link-navigation-${stepNumber(prefix - 213)}-subtle-active-state-cue-suppor`);

export const STEP_THREE_FIELD_INPUT_TICKETS = range(291, 297).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-03-field-planner-input-${stepNumber(prefix - 213)}-${suffix}`));

export const STEP_THREE_DISCLOSURE_PANEL_TICKETS = range(298, 303).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-03-disclosure-panel-${stepNumber(prefix - 213)}-${suffix}`));

export const STEP_THREE_PLANNER_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(304, rank)}-interactive-step-03-planner-game-grid-${suffix}`);

export const STEP_THREE_PLANNER_STEP_HEADER_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(305, rank)}-interactive-step-03-planner-step-header-${suffix}`);

export const STEP_THREE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(306, rank)}-interactive-step-03-planner-control-panel-${suffix}`);

export const STEP_THREE_GAME_PREVIEW_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(307, rank)}-interactive-step-03-game-preview-${suffix}`);

export const STEP_THREE_GAME_IFRAME_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(308, rank)}-interactive-step-03-game-iframe-${suffix}`);

export const STEP_THREE_XP_HUD_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(309, rank)}-interactive-step-03-xp-hud-${suffix}`);

export const STEP_THREE_STEP_NAVIGATION_TICKETS = [
  "P3-310001-nav-step-03-step-navigation-leave-as-is-keep-nav-clean",
  "P3-310002-nav-step-03-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_THREE_PRODUCT_COUNT_CONTROL_SUPPORT_TICKETS = [
  "P3-311002-section-step-03-product-count-control-interactive-audit-map-section",
  "P3-311003-section-step-03-product-count-control-decision-path-section",
  "P3-311004-section-step-03-product-count-control-find-the-gap-section-inspectio",
  "P3-311005-section-step-03-product-count-control-recommended-next-step-framing-",
  "P3-311006-section-step-03-product-count-control-leave-as-is",
  "P3-311007-section-step-03-product-count-control-on-scroll-reveal-support",
] as const;

export const STEP_THREE_PRODUCT_OFFER_CARD_SUPPORT_TICKETS = [
  "P3-312002-pricing-step-03-product-offer-cards-plan-fit-scorer-calm-stateful",
  "P3-312003-pricing-step-03-product-offer-cards-scope-confidence-acknowledgeme",
  "P3-312004-pricing-step-03-product-offer-cards-animated-count-up-real-numbers",
  "P3-312005-pricing-step-03-product-offer-cards-leave-as-is",
] as const;

export const STEP_THREE_PRODUCT_AVENUE_SCENE_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(313, rank)}-interactive-step-03-product-avenue-scene-${suffix}`);

export const STEP_FOUR_LAYOUT_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(314, rank)}-interactive-step-04-layout-planner-game-grid-001-${suffix}`);

export const STEP_FOUR_GAME_PREVIEW_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(315, rank)}-interactive-step-04-interactive-game-preview-planner-${suffix}`);

export const STEP_FOUR_GAME_IFRAME_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(316, rank)}-interactive-step-04-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_FOUR_READING_TICKETS = range(317, 336).flatMap((prefix) => [
  `P2-${groupedTicket(prefix, 1)}-reading-step-04-heading-planner-step-${stepNumber(prefix - 313)}-before-after-ai-answer-reveal-`,
  ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-04-heading-planner-step-${stepNumber(prefix - 313)}-${suffix}`),
]);

export const STEP_FOUR_CTA_TICKETS = range(337, 347).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-04-button-planner-action-${stepNumber(prefix - 313)}-${suffix}`));

export const STEP_FOUR_NAV_TICKETS = range(348, 385).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-04-link-navigation-${stepNumber(prefix - 313)}-subtle-active-state-cue-suppor`);

export const STEP_FOUR_FIELD_INPUT_TICKETS = range(386, 401).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-04-field-planner-input-${stepNumber(prefix - 313)}-${suffix}`));

export const STEP_FOUR_DISCLOSURE_PANEL_TICKETS = range(402, 409).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-04-disclosure-panel-${stepNumber(prefix - 313)}-${suffix}`));

export const STEP_FOUR_PLANNER_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(410, rank)}-interactive-step-04-planner-game-grid-${suffix}`);

export const STEP_FOUR_PLANNER_STEP_HEADER_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(411, rank)}-interactive-step-04-planner-step-header-${suffix}`);

export const STEP_FOUR_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(412, rank)}-interactive-step-04-planner-control-panel-${suffix}`);

export const STEP_FOUR_GAME_PREVIEW_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(413, rank)}-interactive-step-04-game-preview-${suffix}`);

export const STEP_FOUR_GAME_IFRAME_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(414, rank)}-interactive-step-04-game-iframe-${suffix}`);

export const STEP_FOUR_XP_HUD_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(415, rank)}-interactive-step-04-xp-hud-${suffix}`);

export const STEP_FOUR_STEP_NAVIGATION_TICKETS = [
  "P3-416001-nav-step-04-step-navigation-leave-as-is-keep-nav-clean",
  "P3-416002-nav-step-04-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_FOUR_AUDIENCE_FIELDS_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(417, rank)}-interactive-step-04-audience-fields-${suffix}`);

export const STEP_FOUR_REGIONAL_OBJECTIVE_ASSUMPTIONS_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(418, rank)}-interactive-step-04-regional-objective-assumptions-${suffix}`);

export const STEP_FOUR_AUDIENCE_PLAZA_SCENE_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(419, rank)}-interactive-step-04-audience-plaza-scene-${suffix}`);

export const STEP_FIVE_LAYOUT_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(420, rank)}-interactive-step-05-layout-planner-game-grid-001-${suffix}`);

export const STEP_FIVE_GAME_PREVIEW_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(421, rank)}-interactive-step-05-interactive-game-preview-planner-${suffix}`);

export const STEP_FIVE_GAME_IFRAME_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(422, rank)}-interactive-step-05-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_FIVE_READING_TICKETS = range(423, 442).flatMap((prefix) => [
  `P2-${groupedTicket(prefix, 1)}-reading-step-05-heading-planner-step-${stepNumber(prefix - 419)}-before-after-ai-answer-reveal-`,
  ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-05-heading-planner-step-${stepNumber(prefix - 419)}-${suffix}`),
]);

export const STEP_FIVE_CTA_TICKETS = range(443, 460).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-05-button-planner-action-${stepNumber(prefix - 419)}-${suffix}`));

export const STEP_FIVE_NAV_TICKETS = range(461, 499).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-05-link-navigation-${stepNumber(prefix - 419)}-subtle-active-state-cue-suppor`);

export const STEP_FIVE_FIELD_INPUT_TICKETS = range(500, 506).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-05-field-planner-input-${stepNumber(prefix - 419)}-${suffix}`));

export const STEP_FIVE_DISCLOSURE_PANEL_TICKETS = range(507, 512).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-05-disclosure-panel-${stepNumber(prefix - 419)}-${suffix}`));

export const STEP_FIVE_PLANNER_GAME_GRID_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(513, rank)}-interactive-step-05-planner-game-grid-${suffix}`);

export const STEP_FIVE_PLANNER_STEP_HEADER_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(514, rank)}-interactive-step-05-planner-step-header-${suffix}`);

export const STEP_FIVE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(515, rank)}-interactive-step-05-planner-control-panel-${suffix}`);

export const STEP_FIVE_GAME_PREVIEW_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(516, rank)}-interactive-step-05-game-preview-${suffix}`);

export const STEP_FIVE_GAME_IFRAME_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(517, rank)}-interactive-step-05-game-iframe-${suffix}`);

export const STEP_FIVE_XP_HUD_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(518, rank)}-interactive-step-05-xp-hud-${suffix}`);

export const STEP_FIVE_STEP_NAVIGATION_TICKETS = [
  "P3-519001-nav-step-05-step-navigation-leave-as-is-keep-nav-clean",
  "P3-519002-nav-step-05-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_FIVE_STAGE_CONVERSION_CONTROLS_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(520, rank)}-interactive-step-05-stage-conversion-controls-${suffix}`);

export const STEP_FIVE_FUNNEL_STAGE_MAP_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(521, rank)}-interactive-step-05-funnel-stage-map-${suffix}`);

export const STEP_FIVE_FUNNEL_DISTRICT_SCENE_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(522, rank)}-interactive-step-05-funnel-district-scene-${suffix}`);

export const STEP_SIX_LAYOUT_GAME_GRID_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(523, rank)}-interactive-step-06-layout-planner-game-grid-001-${suffix}`);

export const STEP_SIX_GAME_PREVIEW_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(524, rank)}-interactive-step-06-interactive-game-preview-planner-${suffix}`);

export const STEP_SIX_GAME_IFRAME_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(525, rank)}-interactive-step-06-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_SIX_READING_TICKETS = range(526, 552).flatMap((prefix) => [
  `P2-${groupedTicket(prefix, 1)}-reading-step-06-heading-planner-step-${stepNumber(prefix - 522)}-before-after-ai-answer-reveal-`,
  ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-06-heading-planner-step-${stepNumber(prefix - 522)}-${suffix}`),
]);

export const STEP_SIX_CTA_TICKETS = range(553, 587).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-06-button-planner-action-${stepNumber(prefix - 522)}-${suffix}`));

export const STEP_SIX_OFFER_ASSEMBLY_MINI_CTA_TICKETS = range(553, 587).map((prefix) =>
  `P1-${groupedTicket(prefix, 1)}-cta-step-06-button-planner-action-${stepNumber(prefix - 522)}-offer-assembly-mini-cta-brief`);

export const STEP_SIX_NAV_TICKETS = range(588, 625).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-06-link-navigation-${stepNumber(prefix - 522)}-subtle-active-state-cue-suppor`);

export const STEP_SIX_FIELD_INPUT_TICKETS = range(626, 639).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-06-field-planner-input-${stepNumber(prefix - 522)}-${suffix}`));

export const STEP_SIX_DISCLOSURE_PANEL_TICKETS = range(640, 645).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-06-disclosure-panel-${stepNumber(prefix - 522)}-${suffix}`));

export const STEP_SIX_PLANNER_GAME_GRID_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(646, rank)}-interactive-step-06-planner-game-grid-${suffix}`);

export const STEP_SIX_PLANNER_STEP_HEADER_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(647, rank)}-interactive-step-06-planner-step-header-${suffix}`);

export const STEP_SIX_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = [
  ...OFFER_ASSEMBLY_SUFFIX,
  ...MECHANIC_SUFFIXES,
].map(([rank, suffix]) =>
  `P3-${groupedTicket(648, rank)}-interactive-step-06-planner-control-panel-${suffix}`);

export const STEP_SIX_GAME_PREVIEW_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(649, rank)}-interactive-step-06-game-preview-${suffix}`);

export const STEP_SIX_GAME_IFRAME_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(650, rank)}-interactive-step-06-game-iframe-${suffix}`);

export const STEP_SIX_XP_HUD_SURFACE_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(651, rank)}-interactive-step-06-xp-hud-${suffix}`);

export const STEP_SIX_STEP_NAVIGATION_TICKETS = [
  "P3-652001-nav-step-06-step-navigation-leave-as-is-keep-nav-clean",
  "P3-652002-nav-step-06-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_SIX_SCENARIO_CONTROL_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(653, rank)}-interactive-step-06-scenario-control-${suffix}`);

export const STEP_SIX_PROJECTION_KPI_SUPPORT_TICKETS = [
  "P3-654002-pricing-step-06-projection-kpis-plan-fit-scorer-calm-stateful",
  "P3-654003-pricing-step-06-projection-kpis-scope-confidence-acknowledgeme",
  "P3-654004-pricing-step-06-projection-kpis-animated-count-up-real-numbers",
  "P3-654005-pricing-step-06-projection-kpis-leave-as-is",
] as const;

export const STEP_SIX_BANK_DISTRICT_SCENE_SUPPORT_TICKETS = MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(655, rank)}-interactive-step-06-bank-district-scene-${suffix}`);

const STEP_SEVEN_LATE_MECHANIC_SUFFIXES = MECHANIC_SUFFIXES.filter(([rank]) => rank >= 6);
const STEP_SEVEN_XP_HUD_SUFFIXES = MECHANIC_SUFFIXES.filter(([rank]) => rank >= 5);
const STEP_SEVEN_FINALE_CITY_SUFFIXES = [
  [5, "interactive-audit-map"],
  [6, "live-calculator-model"],
  [7, "scope-budget-dial-with-safe-ba"],
  [8, "scored-readiness-checklist-int"],
  [9, "scored-self-assessment-fit-qui"],
  [10, "service-fit-mini-game"],
  [11, "encouraging-inline-feedback-se"],
  [12, "animated-count-up-real-numbers"],
  [13, "multi-step-configurator-planne"],
  [14, "mini-self-check-light-interact"],
  [15, "inline-before-after-reveal-lig"],
] as const;

export const STEP_SEVEN_LAYOUT_GAME_GRID_SUPPORT_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(656, rank)}-interactive-step-07-layout-planner-game-grid-001-${suffix}`);

export const STEP_SEVEN_GAME_PREVIEW_SUPPORT_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(657, rank)}-interactive-step-07-interactive-game-preview-planner-${suffix}`);

export const STEP_SEVEN_GAME_IFRAME_SUPPORT_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(658, rank)}-interactive-step-07-interactive-game-iframe-3d-city--${suffix}`);

export const STEP_SEVEN_READING_TICKETS = range(659, 679).flatMap((prefix) => [
  `P2-${groupedTicket(prefix, 1)}-reading-step-07-heading-planner-step-${stepNumber(prefix - 655)}-before-after-ai-answer-reveal-`,
  ...READING_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-reading-step-07-heading-planner-step-${stepNumber(prefix - 655)}-${suffix}`),
]);

export const STEP_SEVEN_CTA_TICKETS = range(680, 690).flatMap((prefix) =>
  CTA_SUPPORT_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P3-${groupedTicket(prefix, suffixIndex)}-cta-step-07-button-planner-action-${stepNumber(prefix - 655)}-${suffix}`));

export const STEP_SEVEN_OFFER_ASSEMBLY_MINI_CTA_TICKETS = range(680, 690).map((prefix) =>
  `P1-${groupedTicket(prefix, 1)}-cta-step-07-button-planner-action-${stepNumber(prefix - 655)}-offer-assembly-mini-cta-brief`);

export const STEP_SEVEN_NAV_TICKETS = range(691, 729).map((prefix) =>
  `P3-${groupedTicket(prefix, 2)}-nav-step-07-link-navigation-${stepNumber(prefix - 655)}-subtle-active-state-cue-suppor`);

export const STEP_SEVEN_FIELD_INPUT_TICKETS = range(730, 740).flatMap((prefix) =>
  FORM_FIELD_SUFFIXES.map(([suffixIndex, suffix]) =>
    `P${suffixIndex === 1 ? "2" : "3"}-${groupedTicket(prefix, suffixIndex)}-form-step-07-field-planner-input-${stepNumber(prefix - 655)}-${suffix}`));

export const STEP_SEVEN_DISCLOSURE_PANEL_TICKETS = range(741, 746).flatMap((prefix) =>
  DISCLOSURE_PANEL_SUFFIXES.map(([priority, suffixIndex, suffix]) =>
    `${priority}-${groupedTicket(prefix, suffixIndex)}-section-step-07-disclosure-panel-${stepNumber(prefix - 655)}-${suffix}`));

export const STEP_SEVEN_PLANNER_GAME_GRID_SUPPORT_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(747, rank)}-interactive-step-07-planner-game-grid-${suffix}`);

export const STEP_SEVEN_PLANNER_STEP_HEADER_SUPPORT_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(748, rank)}-interactive-step-07-planner-step-header-${suffix}`);

export const STEP_SEVEN_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(749, rank)}-interactive-step-07-planner-control-panel-${suffix}`);

export const STEP_SEVEN_GAME_PREVIEW_SURFACE_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(750, rank)}-interactive-step-07-game-preview-${suffix}`);

export const STEP_SEVEN_GAME_IFRAME_SURFACE_TICKETS = STEP_SEVEN_LATE_MECHANIC_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(751, rank)}-interactive-step-07-game-iframe-${suffix}`);

export const STEP_SEVEN_XP_HUD_SURFACE_TICKETS = STEP_SEVEN_XP_HUD_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(752, rank)}-interactive-step-07-xp-hud-${suffix}`);

export const STEP_SEVEN_STEP_NAVIGATION_TICKETS = [
  "P3-753001-nav-step-07-step-navigation-leave-as-is-keep-nav-clean",
  "P3-753002-nav-step-07-step-navigation-subtle-active-state-cue-suppor",
] as const;

export const STEP_SEVEN_DOWNLOAD_REVIEW_SUPPORT_TICKETS = [
  "P3-754002-section-step-07-download-review-interactive-audit-map-section",
  "P3-754003-section-step-07-download-review-animated-count-up-real-numbers",
  "P3-754004-section-step-07-download-review-decision-path-section",
  "P3-754005-section-step-07-download-review-find-the-gap-section-inspectio",
  "P3-754006-section-step-07-download-review-recommended-next-step-framing-",
  "P3-754007-section-step-07-download-review-leave-as-is",
  "P3-754008-section-step-07-download-review-on-scroll-reveal-support",
] as const;

export const STEP_SEVEN_CONTACT_HANDOFF_FIELD_TICKETS = [
  "P2-755001-form-step-07-contact-handoff-fields-encouraging-inline-validation-",
  "P3-755002-form-step-07-contact-handoff-fields-chunked-multi-step-form-progre",
] as const;

export const STEP_SEVEN_FINALE_CITY_SCENE_SUPPORT_TICKETS = STEP_SEVEN_FINALE_CITY_SUFFIXES.map(([rank, suffix]) =>
  `P3-${groupedTicket(756, rank)}-interactive-step-07-finale-city-scene-${suffix}`);

export const STEP_ONE_NEXT200_SELECTED_TICKETS = [
  ...STEP_ONE_NEXT200_GRID_TICKETS,
  ...STEP_ONE_NEXT200_GAME_PREVIEW_TICKETS,
  ...STEP_ONE_NEXT200_GAME_IFRAME_TICKETS,
  ...STEP_ONE_NEXT200_READING_TICKETS,
  ...STEP_ONE_NEXT200_CTA_TICKETS,
  ...STEP_ONE_NEXT200_NAV_TICKETS,
  ...STEP_ONE_FIELD_INPUT_TICKETS,
  ...STEP_ONE_DISCLOSURE_PANEL_TICKETS,
  ...STEP_ONE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_ONE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_ONE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_ONE_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_ONE_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_ONE_XP_HUD_SUPPORT_TICKETS,
  ...STEP_ONE_STEP_NAV_SUPPORT_TICKETS,
  ...STEP_ONE_TEMPLATE_PICKER_SUPPORT_TICKETS,
  ...STEP_ONE_PERSONA_SELECTOR_SUPPORT_TICKETS,
  ...STEP_TWO_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_TWO_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_TWO_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_TWO_READING_TICKETS,
  ...STEP_TWO_CTA_TICKETS,
  ...STEP_TWO_BATCH2_CTA_TICKETS,
  ...STEP_TWO_BATCH2_NAV_TICKETS,
  ...STEP_TWO_STEP_NAVIGATION_TICKETS,
  ...STEP_TWO_FIELD_INPUT_TICKETS,
  ...STEP_TWO_DISCLOSURE_PANEL_TICKETS,
  ...STEP_TWO_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_TWO_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_TWO_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_TWO_GAME_PREVIEW_SURFACE_TICKETS,
  ...STEP_TWO_GAME_IFRAME_SURFACE_TICKETS,
  ...STEP_TWO_XP_HUD_SURFACE_TICKETS,
  ...STEP_TWO_TRAFFIC_CHANNEL_LIST_SUPPORT_TICKETS,
  ...STEP_TWO_VISITOR_SLIDER_SUPPORT_TICKETS,
  ...STEP_TWO_TRAFFIC_CITY_DISTRICT_SUPPORT_TICKETS,
  ...STEP_THREE_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_THREE_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_THREE_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_THREE_READING_TICKETS,
  ...STEP_THREE_CTA_TICKETS,
  ...STEP_THREE_NAV_TICKETS,
  ...STEP_THREE_FIELD_INPUT_TICKETS,
  ...STEP_THREE_DISCLOSURE_PANEL_TICKETS,
  ...STEP_THREE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_THREE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_THREE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_THREE_GAME_PREVIEW_SURFACE_TICKETS,
  ...STEP_THREE_GAME_IFRAME_SURFACE_TICKETS,
  ...STEP_THREE_XP_HUD_SURFACE_TICKETS,
  ...STEP_THREE_STEP_NAVIGATION_TICKETS,
  ...STEP_THREE_PRODUCT_COUNT_CONTROL_SUPPORT_TICKETS,
  ...STEP_THREE_PRODUCT_OFFER_CARD_SUPPORT_TICKETS,
  ...STEP_THREE_PRODUCT_AVENUE_SCENE_SUPPORT_TICKETS,
  ...STEP_FOUR_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_FOUR_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_FOUR_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_FOUR_READING_TICKETS,
  ...STEP_FOUR_CTA_TICKETS,
  ...STEP_FOUR_NAV_TICKETS,
  ...STEP_FOUR_FIELD_INPUT_TICKETS,
  ...STEP_FOUR_DISCLOSURE_PANEL_TICKETS,
  ...STEP_FOUR_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_FOUR_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_FOUR_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_FOUR_GAME_PREVIEW_SURFACE_TICKETS,
  ...STEP_FOUR_GAME_IFRAME_SURFACE_TICKETS,
  ...STEP_FOUR_XP_HUD_SURFACE_TICKETS,
  ...STEP_FOUR_STEP_NAVIGATION_TICKETS,
  ...STEP_FOUR_AUDIENCE_FIELDS_SUPPORT_TICKETS,
  ...STEP_FOUR_REGIONAL_OBJECTIVE_ASSUMPTIONS_SUPPORT_TICKETS,
  ...STEP_FOUR_AUDIENCE_PLAZA_SCENE_SUPPORT_TICKETS,
  ...STEP_FIVE_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_FIVE_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_FIVE_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_FIVE_READING_TICKETS,
  ...STEP_FIVE_CTA_TICKETS,
  ...STEP_FIVE_NAV_TICKETS,
  ...STEP_FIVE_FIELD_INPUT_TICKETS,
  ...STEP_FIVE_DISCLOSURE_PANEL_TICKETS,
  ...STEP_FIVE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_FIVE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_FIVE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_FIVE_GAME_PREVIEW_SURFACE_TICKETS,
  ...STEP_FIVE_GAME_IFRAME_SURFACE_TICKETS,
  ...STEP_FIVE_XP_HUD_SURFACE_TICKETS,
  ...STEP_FIVE_STEP_NAVIGATION_TICKETS,
  ...STEP_FIVE_STAGE_CONVERSION_CONTROLS_SUPPORT_TICKETS,
  ...STEP_FIVE_FUNNEL_STAGE_MAP_SUPPORT_TICKETS,
  ...STEP_FIVE_FUNNEL_DISTRICT_SCENE_SUPPORT_TICKETS,
  ...STEP_SIX_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_SIX_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_SIX_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_SIX_READING_TICKETS,
  ...STEP_SIX_CTA_TICKETS,
  ...STEP_SIX_OFFER_ASSEMBLY_MINI_CTA_TICKETS,
  ...STEP_SIX_NAV_TICKETS,
  ...STEP_SIX_FIELD_INPUT_TICKETS,
  ...STEP_SIX_DISCLOSURE_PANEL_TICKETS,
  ...STEP_SIX_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_SIX_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_SIX_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_SIX_GAME_PREVIEW_SURFACE_TICKETS,
  ...STEP_SIX_GAME_IFRAME_SURFACE_TICKETS,
  ...STEP_SIX_XP_HUD_SURFACE_TICKETS,
  ...STEP_SIX_STEP_NAVIGATION_TICKETS,
  ...STEP_SIX_SCENARIO_CONTROL_SUPPORT_TICKETS,
  ...STEP_SIX_PROJECTION_KPI_SUPPORT_TICKETS,
  ...STEP_SIX_BANK_DISTRICT_SCENE_SUPPORT_TICKETS,
  ...STEP_SEVEN_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_SEVEN_GAME_PREVIEW_SUPPORT_TICKETS,
  ...STEP_SEVEN_GAME_IFRAME_SUPPORT_TICKETS,
  ...STEP_SEVEN_READING_TICKETS,
  ...STEP_SEVEN_CTA_TICKETS,
  ...STEP_SEVEN_OFFER_ASSEMBLY_MINI_CTA_TICKETS,
  ...STEP_SEVEN_NAV_TICKETS,
  ...STEP_SEVEN_FIELD_INPUT_TICKETS,
  ...STEP_SEVEN_DISCLOSURE_PANEL_TICKETS,
  ...STEP_SEVEN_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ...STEP_SEVEN_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ...STEP_SEVEN_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ...STEP_SEVEN_GAME_PREVIEW_SURFACE_TICKETS,
  ...STEP_SEVEN_GAME_IFRAME_SURFACE_TICKETS,
  ...STEP_SEVEN_XP_HUD_SURFACE_TICKETS,
  ...STEP_SEVEN_STEP_NAVIGATION_TICKETS,
  ...STEP_SEVEN_DOWNLOAD_REVIEW_SUPPORT_TICKETS,
  ...STEP_SEVEN_CONTACT_HANDOFF_FIELD_TICKETS,
  ...STEP_SEVEN_FINALE_CITY_SCENE_SUPPORT_TICKETS,
] as const;

export function joinAuditTicketGroups(...ticketGroups: Array<readonly string[] | string | null | undefined>): string | undefined {
  const tickets = ticketGroups
    .flatMap((group) => {
      if (Array.isArray(group)) return group;
      return String(group || "").split(";");
    })
    .map((ticket) => ticket.trim())
    .filter(Boolean);
  return tickets.length > 0 ? Array.from(new Set(tickets)).join(";") : undefined;
}
